import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import { getTotalDebt } from './refinancing.helpers';
import { TRefinancingDebt } from './refinancing.types';

const MAX_DEBTS = 6;

export const useRefinancingDebts = () => {
  const [targetDebtIndex, setTargetDebtIndex] = useState(0);
  const { control, watch } = useFormContext();

  const {
    fields: debts,
    append,
    remove,
  } = useFieldArray({ control, name: 'refinancing.debts' });

  const addDebt = () => {
    if (debts.length >= MAX_DEBTS) return;
    // The appended debt lands at the current end of the array.
    const newDebtIndex = debts.length;
    append({ facilityType: '' });
    setTargetDebtIndex(newDebtIndex);
  };

  const removeDebt = (index: number) => {
    setTargetDebtIndex(Math.max(0, index - 1));
    remove(index);
  };

  const requestedAmount = convertCurrencyToNumber(watch('amount')) || 0;
  const watchedDebts: TRefinancingDebt[] = watch('refinancing.debts') ?? [];

  // Round each balance to whole pounds up front so the summary total always
  // equals the sum of the debt lines it shows (both display as whole pounds).
  // Summing the raw values and rounding only the total could otherwise leave
  // the total a pound off from the visible breakdown.
  const debtLines = watchedDebts
    .map((debt, index) => {
      const lenderName = debt?.lenderName?.trim();
      return {
        label: lenderName
          ? `Debt ${index + 1} - ${lenderName}`
          : `Debt ${index + 1}`,
        amount: Math.round(
          convertCurrencyToNumber(debt?.outstandingBalance) || 0,
        ),
      };
    })
    .filter((line) => line.amount > 0);

  const totalDebt = getTotalDebt(watchedDebts);
  const amountRemaining = requestedAmount - totalDebt;

  return {
    debts,
    targetDebtIndex,
    setTargetDebtIndex,
    addDebt,
    removeDebt,
    canAddDebt: debts.length < MAX_DEBTS,
    requestedAmount,
    totalDebt,
    amountRemaining,
    debtLines,
  };
};
